/**
 * Croatian labels for `other` keys aligned with worker `Other` (definitions.ts).
 * Unknown keys fall back to {@link humanizeFallbackKey}.
 */
export const OTHER_KEY_LABELS: Record<string, string> = {
  administrativeOfficer: "Administrativni dužnosnik",
  administrativeSecretary: "Administrativni tajnik",
  assembly: "Skupština",
  associationRegistrationNumber: "Registarski broj udruge",
  bank: "Banka",
  boardPresident: "Predsjednik odbora",
  bodiesBoardsCommittees: "Tijela, odbori i povjerenstva",
  coach: "Trener",
  companyNumber: "Broj tvrtke",
  contactBlock: "Kontaktni blok",
  contactListing: "Popis kontakata",
  contactNote: "Napomena uz kontakt",
  contactPhone: "Telefon (kontakt)",
  croatian_chess_federation_link: "Poveznica Hrvatskog šahovskog saveza",
  deputyPresident: "Potpredsjednik",
  employees: "Zaposlenici",
  facebook: "Facebook",
  financialReports: "Financijski izvještaji",
  footerEmail: "E-pošta (podnožje)",
  giro: "Žiro",
  opening_hours: "Radno vrijeme (stranica)",
  instagram: "Instagram",
  youtube: "YouTube",
  izvršniOdborFooterHtml: "Izvršni odbor (sadržaj podnožja)",
  kshEmail: "E-pošta KŠ",
  mailFromFooter: "E-pošta iz podnožja",
  maticni_broj: "Matični broj",
  matičniBroj: "Matični broj",
  legislatureHeading: "Naslov zakonodavnog tijela",
  mb: "MB",
  mbs: "MBS",
  mib: "MIB",
  officeHours: "Radno vrijeme",
  liquidator: "Likvidator",
  iban: "IBAN",
  oib: "OIB",
  secretary: "Tajnik",
  secretaryHsor: "Tajnik HSOR",
  secretaryHspor: "Tajnik HSPOR",
  president: "Predsjednik",
  presidentHsor: "Predsjednik HSOR",
  presidentHspor: "Predsjednik HSPOR",
  presidentMobile: "Mobitel predsjednika",
  professionalSecretary: "Stručni tajnik",
  refereesLead: "Voditelj sudaca",
  registerNumber: "Registarski broj",
  rno: "RNO",
  secretaryMobile: "Mobitel tajnika",
  secretaryGeneral: "Glavni tajnik",
  summary: "Sažetak",
  swift: "SWIFT",
  technicalSecretary: "Tehnički tajnik",
  technicalCommittee: "Tehničko povjerenstvo",
  vicePresident: "Potpredsjednik",
  vicePresidentMobile: "Mobitel potpredsjednika",
  vicePresidentHsor: "Potpredsjednik HSOR",
  statutePdf: "Statut (PDF)",
  surv_board: "Nadzorni odbor (kratko)",
  lead_board: "Vodeći odbor",
  website: "Web stranica",
  workingHours: "Radno vrijeme",
  ziro: "Žiro račun",
  zksEmail: "E-pošta ZKS",
  kontaktDepartmentText: "Kontakt odjeli",
  trainers: "Treneri",
  instructors: "Instruktori",
  supervisoryBoardMembers: "Članovi nadzornog odbora",
  executiveBoardMembers: "Članovi izvršnog odbora",
  executiveBoard: "Izvršni odbor",
  executiveBoardEmailsFromFooter: "E-pošta izvršnog odbora (podnožje)",
  honourCourtMembers: "Članovi častnog suda",
  memberClubs: "Članski klubovi",
  ustrojParagraphs: "Ustroj (odlomci)",
  executiveSecretary: "Izvršni tajnik (blok)",
  governance: "Uprava i poveznice",
  pratiteNas: "Društvene mreže",
  staff: "Osoblje",
  boards: "Odbori (tekstovi)",
  stats: "Statistika",
  boardTables: "Tablice odbora",
  organizationSections: "Organizacijske cjeline",
  leadership: "Vodstvo",
  supervisoryBoard: "Nadzorni odbor",
  board: "Odbor",
  disciplinaryCommittee: "Disciplinska komisija",
  sidebarReferences: "Reference (bočni izbornik)",
  office: "Ured / služba",
  supervision: "Nadzor",
  assemblyMembers: "Članovi skupštine",
  boardMembers: "Članovi odbora",
  members: "Članovi (s klubom)",
  contacts: "Kontakti",
  athletesByClub2021: "Sportaši po klubu (2021)",
};

const DELIM_LIST = "; ";
const DELIM_PAIR = " · ";
const MAX_FORMAT_DEPTH = 6;

function labelForKey(key: string): string {
  return OTHER_KEY_LABELS[key] ?? humanizeFallbackKey(key);
}

function humanizeFallbackKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-zčćžšđ])([A-ZČĆŽŠĐ])/g, "$1 $2")
    .replace(/([A-ZČĆŽŠĐ])([A-ZČĆŽŠĐ][a-zčćžšđ])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function tryParseJsonArray(s: string): unknown[] | null {
  try {
    const v = JSON.parse(s) as unknown;
    if (Array.isArray(v)) return v;
  } catch {
    /* not JSON */
  }
  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function formatPersonLike(obj: Record<string, unknown>): string {
  const title = obj.title != null ? String(obj.title).trim() : "";
  const name = obj.name != null ? String(obj.name).trim() : "";
  const role = obj.role != null ? String(obj.role).trim() : "";
  const org = obj.organization != null ? String(obj.organization).trim() : "";
  const mail = obj.mail != null ? String(obj.mail).trim() : "";
  const link = obj.link != null ? String(obj.link).trim() : "";

  if (title && link && !name && !role) return `${title} - ${link}`;
  if (title && !name && !role && !link && !org && !mail) return title;

  const bits: string[] = [];
  if (name) bits.push(role ? `${name} (${role})` : name);
  else if (role) bits.push(role);
  if (org && !bits.length) bits.push(org);
  else if (org) bits.push(`- ${org}`);
  if (mail) bits.push(mail);
  if (link) bits.push(link);
  return bits.join(" ").trim();
}

function formatBoardTableRow(row: Record<string, unknown>): string {
  const order = [
    "R.BR.",
    "IME I PREZIME",
    "FUNKCIJA",
    "ADRESA",
    "TELEFON",
    "E-MAIL",
  ];
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const k of order) {
    const v = row[k];
    if (v != null && String(v).trim() !== "") {
      parts.push(`${k}: ${String(v).trim()}`);
      seen.add(k);
    }
  }
  for (const [k, v] of Object.entries(row)) {
    if (seen.has(k)) continue;
    if (v != null && String(v).trim() !== "")
      parts.push(`${k}: ${String(v).trim()}`);
  }
  return parts.join(", ");
}

function formatBoardTablesValue(v: Record<string, unknown>): string {
  const sections: string[] = [];
  for (const [boardTitle, rows] of Object.entries(v)) {
    if (!Array.isArray(rows)) {
      sections.push(
        `${boardTitle}: ${formatOtherValueDeep(rows, 0, new WeakSet())}`,
      );
      continue;
    }
    const lines = rows.map((r) =>
      isPlainObject(r) ? formatBoardTableRow(r) : String(r),
    );
    sections.push(`${boardTitle}: ${lines.filter(Boolean).join(" | ")}`);
  }
  return sections.join(" - ");
}

function formatAthletesByClubRow(obj: Record<string, unknown>): string {
  const club = obj.club != null ? String(obj.club).trim() : "";
  const count = obj.count != null ? String(obj.count).trim() : "";
  if (club && count) return `${club}: ${count}`;
  return club || count || "";
}

function formatExecutiveSecretary(v: Record<string, unknown>): string {
  const parts: string[] = [];
  if (v.name != null && String(v.name).trim())
    parts.push(String(v.name).trim());
  if (v.address != null && String(v.address).trim())
    parts.push(String(v.address).trim());
  if (Array.isArray(v.details)) {
    const d = v.details
      .map((x) => (x != null ? String(x).trim() : ""))
      .filter(Boolean);
    if (d.length) parts.push(d.join(DELIM_LIST));
  }
  return parts.join(DELIM_PAIR);
}

function isAthletesRow(obj: Record<string, unknown>): boolean {
  const keys = Object.keys(obj).filter(
    (k) => obj[k] !== null && obj[k] !== undefined && obj[k] !== "",
  );
  return (
    keys.length > 0 &&
    keys.every((k) => ["club", "count"].includes(k)) &&
    "club" in obj
  );
}

function isMemberClubRow(obj: Record<string, unknown>): boolean {
  if (obj.name == null || String(obj.name).trim() === "") return false;
  const keys = Object.keys(obj).filter(
    (k) => obj[k] !== null && obj[k] !== undefined && obj[k] !== "",
  );
  return keys.every((k) => ["name", "club"].includes(k));
}

function formatMemberClubRow(obj: Record<string, unknown>): string {
  const name = String(obj.name).trim();
  const club = obj.club != null ? String(obj.club).trim() : "";
  return club ? `${name} (${club})` : name;
}

function isStaffShape(obj: Record<string, unknown>): boolean {
  const keys = Object.keys(obj);
  return (
    ("chiefSecretary" in obj || "technicalSecretary" in obj) &&
    keys.every((k) => ["chiefSecretary", "technicalSecretary"].includes(k))
  );
}

function formatStaff(obj: Record<string, unknown>): string {
  const bits: string[] = [];
  if (obj.chiefSecretary != null && String(obj.chiefSecretary).trim())
    bits.push(`Glavni tajnik: ${String(obj.chiefSecretary).trim()}`);
  if (obj.technicalSecretary != null && String(obj.technicalSecretary).trim())
    bits.push(`Tehnički tajnik: ${String(obj.technicalSecretary).trim()}`);
  return bits.join(DELIM_PAIR);
}

function isSocialLinksShape(obj: Record<string, unknown>): boolean {
  const keys = Object.keys(obj).filter(
    (k) => obj[k] != null && String(obj[k]).trim() !== "",
  );
  if (keys.length === 0) return false;
  return keys.every((k) => ["facebook", "instagram", "youtube"].includes(k));
}

function formatSocialLinks(obj: Record<string, unknown>): string {
  const bits: string[] = [];
  for (const k of ["facebook", "instagram", "youtube"] as const) {
    const v = obj[k];
    if (v != null && String(v).trim())
      bits.push(`${labelForKey(k)}: ${String(v).trim()}`);
  }
  return bits.join(DELIM_PAIR);
}

function rowLooksLikeBoardTable(r: unknown): boolean {
  if (!isPlainObject(r)) return false;
  return (
    "IME I PREZIME" in r ||
    "FUNKCIJA" in r ||
    "R.BR." in r ||
    ("E-MAIL" in r && "TELEFON" in r)
  );
}

function isBoardTablesShape(obj: Record<string, unknown>): boolean {
  const vals = Object.values(obj);
  if (vals.length === 0) return false;
  return vals.every(
    (v) => Array.isArray(v) && v.every((e) => rowLooksLikeBoardTable(e)),
  );
}

function hasGovernancePersonShape(obj: Record<string, unknown>): boolean {
  return (
    obj.role != null ||
    obj.organization != null ||
    obj.mail != null ||
    obj.link != null
  );
}

/**
 * Normalize scraper `other` values for one table cell: nested arrays/objects → one readable string.
 */
export function formatOtherValueDeep(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): string {
  if (value === null || value === undefined) return "";
  if (depth > MAX_FORMAT_DEPTH) return "…";

  if (typeof value === "string") {
    let s = value.trim();
    if (!s) return "";
    if (/^\s*\[/.test(s)) {
      const parsed = tryParseJsonArray(s);
      if (parsed?.length) {
        return formatOtherValueDeep(parsed, depth + 1, seen);
      }
    }
    if (s.includes(",") || s.includes(";")) {
      const parts = s
        .split(/[,;]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length > 1) return parts.join(DELIM_LIST);
    }
    return s;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    return value
      .map((item) => formatOtherValueDeep(item, depth + 1, seen))
      .map((s) => s.trim())
      .filter(Boolean)
      .join(DELIM_LIST);
  }

  if (typeof value === "object") {
    if (seen.has(value)) return "";
    seen.add(value);
    const obj = value as Record<string, unknown>;

    if (isAthletesRow(obj)) return formatAthletesByClubRow(obj);

    if (isMemberClubRow(obj)) return formatMemberClubRow(obj);

    if (isSocialLinksShape(obj)) return formatSocialLinks(obj);

    if (isStaffShape(obj)) return formatStaff(obj);

    if (Array.isArray(obj.details)) return formatExecutiveSecretary(obj);

    if (isBoardTablesShape(obj)) {
      return formatBoardTablesValue(obj);
    }

    if (hasGovernancePersonShape(obj)) {
      const p = formatPersonLike(obj);
      if (p) return p;
    }

    if (
      obj.address != null &&
      String(obj.address).trim() !== "" &&
      !obj.role &&
      !obj.link
    ) {
      const ex = formatExecutiveSecretary(obj);
      if (ex) return ex;
    }

    if (obj.name != null || obj.role != null) {
      const p = formatPersonLike(obj);
      if (p) return p;
    }

    const entries = Object.entries(obj).filter(
      ([_, v]) => v !== null && v !== undefined && v !== "",
    );
    if (entries.length === 0) return "";

    const nested = entries
      .map(([k, v]) => {
        const inner = formatOtherValueDeep(v, depth + 1, seen);
        if (!inner) return null;
        return `${labelForKey(k)}: ${inner}`;
      })
      .filter((s): s is string => Boolean(s));

    return nested.join(DELIM_PAIR);
  }

  return "";
}

/**
 * @deprecated Use {@link formatOtherValueDeep}; kept for callers that imported the old name.
 */
export function formatOtherCellValue(value: unknown): string {
  return formatOtherValueDeep(value, 0, new WeakSet());
}

export function otherEntriesForTable(
  other: Record<string, unknown> | null | undefined,
): { key: string; label: string; value: string }[] {
  if (!other || typeof other !== "object") return [];
  const out: { key: string; label: string; value: string }[] = [];
  for (const [key, raw] of Object.entries(other)) {
    const value = formatOtherValueDeep(raw, 0, new WeakSet());
    if (!value) continue;
    out.push({ key, label: labelForKey(key), value });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, "hr"));
}
